// styled components
import {
    Link,
    SingleLink,
    StyledAccordion,
    StyledAccordionDetails,
    StyledAccordionSummary,
    StyledDrawer,
    StyledTeamItem,
    StyledTeamsBlock,
    StyledTeamsList,
} from './styles';

// components
import Logo from '@components/Logo';
import { NavLink, useLocation } from 'react-router-dom';
import AuthService from '../../network/AuthService';

// hooks
import { useSidebar } from '@contexts/sidebarContext';
import { useEffect, useMemo, useState } from 'react';
import { useWindowSize } from 'react-use';

// constants
import LINKS from '@constants/links';

const Sidebar = () => {
  const { open, setOpen } = useSidebar();

  const authService = useMemo(() => new AuthService(), []);
  const [expanded, setExpanded] = useState(undefined);
  const [teamsParsed, setTeamsParsed] = useState([]);
  const [currentTeamID, setCurrentTeamID] = useState(null);
  const { pathname } = useLocation();
  const { width } = useWindowSize();

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    width < 1280 && setExpanded(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    const parsed = authService.getTeamsParsedJSON();
    setTeamsParsed(Array.isArray(parsed) ? parsed : []);
    setCurrentTeamID(authService.getTeamID() || null);
  }, [authService]);

  // Switch team: update cookie, update local state (for instant UI), go to /kader and refresh
  const handleSelectTeam = (teamId) => {
    try {
      authService.setCookie('teamID', teamId, { days: 7, secure: false });
      setCurrentTeamID(teamId);
    } finally {
      window.location.href = '/kader'; // navigates and refreshes
    }
  };

  return (
    <StyledDrawer
      variant={width < 1920 ? 'temporary' : 'permanent'}
      anchor="left"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      sx={{ '& .MuiDrawer-paper': { width: 250 } }}
      className="main-sidebar"
    >
      <div className="logo-wrapper">
        <Logo size="sm" />
      </div>

      <nav className="d-flex flex-column g-8 flex-1">
        {LINKS.map((link, index) => (
          <StyledAccordion
            key={link.title}
            expanded={expanded === `panel${index}`}
            onChange={handleChange(`panel${index}`)}
          >
            <StyledAccordionSummary>
              <Link className={`${expanded === `panel${index}` ? 'active' : ''} h4`}>
                <i className={`icon icon-${link.icon}`} /> {link.title}
              </Link>
              <i className="icon icon-chevron-down" />
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              {link.pages.map((page) => (
                <NavLink to={page.path} key={page.title}>
                  {page.title}
                </NavLink>
              ))}
            </StyledAccordionDetails>
          </StyledAccordion>
        ))}
      </nav>

      {/* Only show this block if teamsParsed count > 1 */}
      {teamsParsed.length > 1 && (
        <StyledTeamsBlock>
          <div className="h5 teams-title">Meine Mannschaften</div>
          <StyledTeamsList>
            {teamsParsed.map((t, idx) => {
              const id = t?.TeamID || String(idx);
              const isActive = t?.TeamID === currentTeamID;

              return (
                <StyledTeamItem
                  key={id}
                  $active={isActive}
                  title={t?.TeamID || ''}
                  onClick={() => {
                    if (!isActive && t?.TeamID) handleSelectTeam(t.TeamID);
                  }}
                  role={!isActive ? 'button' : undefined}
                  aria-disabled={isActive ? 'true' : 'false'}
                >
                  <span className="team-name">{t?.teamname ?? '—'}</span>
                  <span className="league-code">{t?.['League Code'] ?? '—'}</span>
                </StyledTeamItem>
              );
            })}
          </StyledTeamsList>
        </StyledTeamsBlock>
      )}

      <SingleLink className={pathname === '/settings' ? 'pinned active' : 'pinned'} as="div">
        <Link
          className={`h4 ${pathname === '/logout' ? 'active' : ''}`}
          onClick={() => {
            authService.logoutUser();
            window.location.href = '/login';
          }}
          href="/login"
        >
          <i className="" /> Logout
        </Link>
      </SingleLink>
    </StyledDrawer>
  );
};

export default Sidebar;
